import { FC, useMemo, useState } from 'react';
import { Dropdown, MenuProps } from 'antd';
import { TbTypography } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import DropdownPanel, { DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import { fontFamily } from '@/theme';

interface FontFamilyOption {
    category: 'Monospace' | 'Sans Serif' | 'Serif';
    description: string;
    key: string;
    label: string;
    value: string;
}

const institutionalFontOptions: FontFamilyOption[] = [
    {
        category: 'Sans Serif',
        description: 'Platform default with excellent density and legibility.',
        key: 'inter',
        label: 'Inter',
        value: fontFamily,
    },
    {
        category: 'Sans Serif',
        description: 'Native system typography optimized for every operating system.',
        key: 'system-sans',
        label: 'System Sans',
        value: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
    {
        category: 'Sans Serif',
        description: 'Neutral Swiss typography suited to dense analytical interfaces.',
        key: 'helvetica-neue',
        label: 'Helvetica Neue',
        value: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    },
    {
        category: 'Sans Serif',
        description: 'Highly compatible typography for distributed enterprise teams.',
        key: 'arial',
        label: 'Arial',
        value: 'Arial, Helvetica, sans-serif',
    },
    {
        category: 'Serif',
        description: 'Authoritative editorial typography for research and reporting.',
        key: 'georgia',
        label: 'Georgia',
        value: 'Georgia, "Times New Roman", serif',
    },
    {
        category: 'Serif',
        description: 'Institutional book typography with strong long-form readability.',
        key: 'charter',
        label: 'Charter',
        value: 'Charter, "Bitstream Charter", "Sitka Text", Cambria, serif',
    },
    {
        category: 'Serif',
        description: 'Document-first typography familiar across financial workflows.',
        key: 'cambria',
        label: 'Cambria',
        value: 'Cambria, Georgia, "Times New Roman", serif',
    },
    {
        category: 'Monospace',
        description: 'Tabular typography for figures, code, and audit-oriented data.',
        key: 'system-mono',
        label: 'System Monospace',
        value: 'ui-monospace, "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", monospace',
    },
];

interface FontFamilyDropdownProps {
    disabled?: boolean;
    onChange: (fontFamilyValue: string) => void;
    value?: string;
}

const FontFamilyDropdown: FC<FontFamilyDropdownProps> = ({ disabled, onChange, value }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const selectedFont =
        institutionalFontOptions.find((option) => option.value === value) ?? institutionalFontOptions[0];

    const items = useMemo<MenuProps['items']>(() => {
        const categories: FontFamilyOption['category'][] = ['Sans Serif', 'Serif', 'Monospace'];
        const normalizedQuery = query.trim().toLowerCase();
        const matchingOptions = institutionalFontOptions.filter((option) =>
            [option.category, option.description, option.label].some((searchableValue) =>
                searchableValue.toLowerCase().includes(normalizedQuery),
            ),
        );

        if (matchingOptions.length === 0) {
            return [
                {
                    disabled: true,
                    key: 'no-font-results',
                    label: <small style={{ color: token.colorTextTertiary }}>No fonts match “{query.trim()}”.</small>,
                },
            ];
        }

        return categories
            .map((category) => ({
                key: category,
                label: <small style={{ color: token.colorTextTertiary }}>{category}</small>,
                type: 'group' as const,
                children: matchingOptions
                    .filter((option) => option.category === category)
                    .map((option) => ({
                        key: option.key,
                        label: (
                            <DropdownOptionContent
                                description={option.description}
                                selected={option.value === value}
                                title={<span style={{ fontFamily: option.value }}>{option.label}</span>}
                            />
                        ),
                    })),
            }))
            .filter(({ children }) => children.length > 0);
    }, [query, token.colorTextTertiary, value]);

    return (
        <Dropdown
            destroyOnHidden
            disabled={disabled}
            menu={{ items: [] }}
            open={open}
            trigger={['click']}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);

                if (!nextOpen) {
                    setQuery('');
                }
            }}
            popupRender={() => (
                <DropdownPanel
                    footer={<LearnMoreFooter topic='workspace typography' variant='dropdown' />}
                    maxHeight='min(28rem, 60vh)'
                    search={{
                        ariaLabel: 'Search Fonts',
                        autoFocus: true,
                        placeholder: 'Search Fonts...',
                        setValue: setQuery,
                        value: query,
                    }}
                    width={360}
                >
                    <DropdownPanelMenu
                        items={items}
                        selectedKeys={[selectedFont.key]}
                        onClick={({ key }) => {
                            const nextFont = institutionalFontOptions.find((option) => option.key === key);

                            if (!nextFont) {
                                return;
                            }

                            onChange(nextFont.value);
                            setOpen(false);
                            setQuery('');
                        }}
                    />
                </DropdownPanel>
            )}
        >
            <DropdownButton aria-label='Font family' block disabled={disabled} style={{ justifyContent: 'flex-start' }}>
                <TbTypography
                    aria-hidden='true'
                    size='1rem'
                    style={{ color: token.colorTextTertiary, flex: 'none', marginRight: '0.5rem' }}
                />
                <span
                    style={{
                        fontFamily: selectedFont.value,
                        overflow: 'hidden',
                        textAlign: 'left',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {selectedFont.label}
                </span>
            </DropdownButton>
        </Dropdown>
    );
};

export default FontFamilyDropdown;
